import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import HomeTagFilter from '@/components/dashboard/HomeTagFilter';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import EntityLink from '@/components/common/EntityLink';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule, themeQuartz, colorSchemeLightCold } from 'ag-grid-enterprise';
import type { CellDoubleClickedEvent, ColDef, ICellRendererParams } from 'ag-grid-community';
import { ArrowUpRight, Activity, Bell, Globe2, GripVertical, LayoutGrid, RefreshCcw, Search, Table2, WifiOff, X, PanelsTopLeft, Server, Gauge, Thermometer } from 'lucide-react';

import {
  fetchPowerPlants,
  fetchPowerPlantForecastSolarDailyBulk,
  fetchPowerPlantTagFilter,
  fetchPowerPlantTablePreferences,
  fetchPowerPlantsAlarmCounts,
  savePowerPlantTagFilter,
  savePowerPlantTablePreferences,
  type PowerPlantAlarmCounts,
  type PowerPlantForecastSolarDailyItem,
  type PowerPlant,
  type PowerPlantTableColumnState,
} from '@/api/powerplants';
import { fetchTagCategories, fetchTags, type TagItem } from '@/api/tags';
import { fetchWeatherBulk, type WeatherData } from '@/api/weather';
import { WeatherCell } from '@/ui/powerplants/weather-interaction';
import WorldPlantMap from '@/components/dashboard/WorldPlantMap';
import { useI18n } from '@/i18n';

const enterpriseModules = [AllEnterpriseModule];

type FilterTag = Pick<TagItem, 'id' | 'label' | 'color'>;

const gridTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

function parseFormattedMetric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const compact = value.trim().replace(/\s+/g, '');
  const unitMatch = compact.match(/(kwh|mwh|gwh|twh)$/i);
  const unit = unitMatch?.[1]?.toLowerCase();
  const normalized = compact
    .replace(/(kwh|mwh|gwh|twh)$/i, '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  const multiplier =
    unit === 'twh' ? 1_000_000_000 :
    unit === 'gwh' ? 1_000_000 :
    unit === 'mwh' ? 1_000 :
    1;
  return parsed * multiplier;
}

function formatMetricValue(value: number | null | undefined, unit: string) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value)} ${unit}`;
}

function formatEnergyValue(value: unknown): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const numeric = Number(value);
  const magnitude = Math.abs(numeric);
  const divisor = magnitude >= 1_000_000 ? 1_000_000 : magnitude >= 1000 ? 1000 : 1;
  const unit = divisor === 1_000_000 ? 'GWh' : divisor === 1000 ? 'MWh' : 'kWh';
  return `${(numeric / divisor).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
}

function formatForecastWithSource(
  value: string | null | undefined,
  source: 'manual' | 'pvgis' | null | undefined,
) {
  if (!value) return '-';
  if (source === 'manual') return `${value} (Manuel)`;
  if (source === 'pvgis') return `${value} (PVGIS)`;
  return value;
}

function calculateRealizationPercentage(actualValue: string | null | undefined, forecastValue: string | null | undefined): number | null {
  const actual = parseFormattedMetric(actualValue);
  const forecast = parseFormattedMetric(forecastValue);
  if (actual === null || forecast === null || forecast <= 0) return null;
  return (actual / forecast) * 100;
}

function calculateRealizationPercentageFromDailyForecast(
  actualValue: string | null | undefined,
  forecastValue: number | null | undefined,
): number | null {
  const actual = parseFormattedMetric(actualValue);
  const forecast = typeof forecastValue === 'number' ? forecastValue : null;
  if (actual === null || forecast === null || forecast <= 0) return null;
  return (actual / forecast) * 100;
}

function formatPercentageValue(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `%${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(value)}`;
}

function getPercentageTone(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return {
      wrap: 'border-slate-200 bg-slate-50 text-slate-400',
      bar: 'bg-slate-300',
      width: '0%',
    };
  }

  const normalized = Math.max(0, Math.min(value, 140));
  if (value < 60) {
    return { wrap: 'border-rose-200 bg-rose-50 text-rose-700', bar: 'bg-rose-500', width: `${normalized}%` };
  }
  if (value < 85) {
    return { wrap: 'border-amber-200 bg-amber-50 text-amber-700', bar: 'bg-amber-500', width: `${normalized}%` };
  }
  if (value <= 100) {
    return { wrap: 'border-emerald-200 bg-emerald-50 text-emerald-700', bar: 'bg-emerald-500', width: `${normalized}%` };
  }
  return { wrap: 'border-sky-200 bg-sky-50 text-sky-700', bar: 'bg-sky-500', width: `${Math.min(normalized, 100)}%` };
}

function PercentageBadge({ value }: { value: number | null | undefined }) {
  const tone = getPercentageTone(value);
  return (
    <div className={`inline-flex min-w-[86px] flex-col rounded-lg border px-2 py-1 ${tone.wrap}`}>
      <span className="text-[11px] font-black leading-none">{formatPercentageValue(value)}</span>
      <span className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/70">
        <span className={`block h-full rounded-full ${tone.bar}`} style={{ width: tone.width }} />
      </span>
    </div>
  );
}

function normalizePlantCoordinates(latV: unknown, lonV: unknown) {
  const lat = typeof latV === 'number' ? latV : Number(String(latV).replace(',', '.'));
  const lon = typeof lonV === 'number' ? lonV : Number(String(lonV).replace(',', '.'));
  if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
  if (lat >= 35 && lat <= 43.5 && lon >= 25 && lon <= 45.5) return { lat, lon };
  if (lon >= 35 && lon <= 43.5 && lat >= 25 && lat <= 45.5) return { lat: lon, lon: lat };
  return { lat, lon };
}

function weatherLocationKey(lat: unknown, lon: unknown): string | null {
  const n = normalizePlantCoordinates(lat, lon);
  return n ? `${n.lat},${n.lon}` : null;
}

function isPlantDataStale(lastSeen: string | null | undefined): boolean {
  if (!lastSeen || !lastSeen.trim()) return true;
  const normalized = lastSeen.includes('T') ? lastSeen : lastSeen.replace(' ', 'T');
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return true;
  return Date.now() - parsed.getTime() > 2 * 60 * 60 * 1000;
}

function StatusBadge({ value }: { value: number }) {
  const color = value === 1 ? '#10b981' : '#f43f5e';
  return (
    <div className="flex h-full items-center justify-center">
      <div
        className="h-3 w-3 rounded-full shadow-sm ring-1 ring-black/5"
        style={{
          background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95) 0%, ${color} 45%, ${color}DD 75%, rgba(15,23,42,0.2) 100%)`,
          boxShadow: `inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(15,23,42,0.15), 0 1px 2px rgba(15,23,42,0.1)`,
        }}
      />
    </div>
  );
}

function TagColorDot({ color, size = 18 }: { color: string; size?: number }) {
  return (
    <span
      className="inline-flex rounded-full border border-white/70 shadow-sm ring-1 ring-black/5"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95) 0%, ${color} 42%, ${color}DD 72%, rgba(15,23,42,0.24) 100%)`,
        boxShadow: `inset 0 1px 1px rgba(255,255,255,0.45), inset 0 -1px 2px rgba(15,23,42,0.18), 0 1px 2px rgba(15,23,42,0.12)`,
      }}
    />
  );
}

function PlantNameCell({
  value,
  data,
  onNavigate,
}: ICellRendererParams<PowerPlant, PowerPlant['power_plant_name']> & {
  onNavigate?: (uuidId: string) => void;
}) {
  const tags = Array.isArray(data?.power_plant_tags) ? data.power_plant_tags : [];
  const visible = tags.slice(0, 3);
  const remaining = tags.length - visible.length;
  const uuidId = data?.power_plant_uuid_id;
  const stale = isPlantDataStale(data?.power_plant_last_seen ?? null);

  return (
    <div className="flex h-full w-full min-w-0 items-center gap-2 overflow-hidden">
      {stale && (
        <span
          className="inline-flex items-center text-rose-600"
          title="Bu santralden 2 saatten uzun suredir veri alinmiyor"
        >
          <WifiOff size={14} />
        </span>
      )}
      {uuidId ? <button type="button" title={value || ''} onClick={event => { event.stopPropagation(); onNavigate?.(uuidId); }} className="min-w-0 truncate text-left font-semibold text-blue-600 hover:text-blue-800 hover:underline focus-visible:outline-blue-500">{value || '—'}</button>
        : <span className="truncate font-semibold text-slate-700">{value || '—'}</span>}
      {visible.map((tag) => (
        <TagColorDot key={tag.id} color={tag.color} size={14} />
      ))}
      {remaining > 0 && <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap">+{remaining}</span>}
      <div className="ml-auto flex shrink-0 items-center gap-1.5 pl-2">
        {data?.has_panels && <span title="Panel tanımlı" aria-label="Panel tanımlı" className="rounded bg-amber-50 p-1 text-amber-600"><PanelsTopLeft size={14} /></span>}
        {data?.has_smartlogger && <span title="SmartLogger tanımlı" aria-label="SmartLogger tanımlı" className="rounded bg-blue-50 p-1 text-blue-600"><Server size={14} /></span>}
        {data?.has_power_meter && <span title="Power Meter tanımlı" aria-label="Power Meter tanımlı" className="rounded bg-emerald-50 p-1 text-emerald-600"><Gauge size={14} /></span>}
        {data?.has_sensor && <span title="Sensör tanımlı" aria-label="Sensör tanımlı" className="rounded bg-cyan-50 p-1 text-cyan-600"><Thermometer size={14} /></span>}
      </div>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-16 rounded-2xl bg-gray-200" />
      <div className="h-[72vh] rounded-2xl bg-gray-200" />
    </div>
  );
}

type LayoutKind = 'table' | 'card';
type CardMetricId =
  | 'city'
  | 'investor'
  | 'weather'
  | 'e_day'
  | 'daily_forecast'
  | 'daily_realization'
  | 'e_week'
  | 'e_month'
  | 'monthly_forecast'
  | 'monthly_realization'
  | 'e_year'
  | 'yearly_forecast'
  | 'yearly_realization'
  | 'e_total'
  | 'pac'
  | 'ac_dc_ratio'
  | 'ia'
  | 'ib'
  | 'ic'
  | 'qac'
  | 'sac'
  | 'pf'
  | 'frequency'
  | 'van'
  | 'vbn'
  | 'vcn'
  | 'vab'
  | 'vbc'
  | 'vca'
  | 'gti'
  | 'generation'
  | 'voltage'
  | 'current'
  | 'consumption'
  | 'wpneg'
  | 'wpneg_pm'
  | 'wppos'
  | 'wppos_pm'
  | 'wqneg'
  | 'wqpos'
  | 'h04_cb'
  | 'last_seen';

const DEFAULT_CARD_METRIC_ORDER: CardMetricId[] = [
  'city',
  'investor',
  'weather',
  'e_day',
  'daily_forecast',
  'daily_realization',
  'e_week',
  'e_month',
  'monthly_forecast',
  'monthly_realization',
  'e_year',
  'yearly_forecast',
  'yearly_realization',
  'e_total',
  'pac',
  'ac_dc_ratio',
  'ia',
  'ib',
  'ic',
  'qac',
  'sac',
  'pf',
  'frequency',
  'van',
  'vbn',
  'vcn',
  'vab',
  'vbc',
  'vca',
  'gti',
  'generation',
  'voltage',
  'current',
  'consumption',
  'wpneg',
  'wpneg_pm',
  'wppos',
  'wppos_pm',
  'wqneg',
  'wqpos',
  'h04_cb',
  'last_seen',
];

function normalizeOrder<T extends string>(saved: string[] | undefined, fallback: readonly T[]): T[] {
  const seen = new Set<T>();
  const ordered: T[] = [];

  for (const item of saved ?? []) {
    const candidate = item as T;
    if (!fallback.includes(candidate) || seen.has(candidate)) continue;
    seen.add(candidate);
    ordered.push(candidate);
  }

  const missing = fallback.filter((item) => !seen.has(item));
  return [...ordered, ...missing];
}

function confirmResetLayout(kind: LayoutKind): Promise<boolean> {
  return kind === 'card'
    ? confirmToast('Kart hücre düzeni sıfırlansın mı?', 'Kartlardaki veri hücrelerinin sırası varsayılana döner.')
    : confirmToast('Kolon düzeni sıfırlansın mı?', 'Gizlenen, taşınan ve genişliği değişen kolonlar varsayılana döner.');
}

function moveItemBefore<T extends string>(items: T[], activeId: T, targetId: T): T[] {
  const filtered = items.filter((item) => item !== activeId && item !== targetId);
  const targetIndex = items.indexOf(targetId);
  if (targetIndex < 0) return items;
  filtered.splice(targetIndex, 0, activeId);
  return filtered;
}

export default function PowerPlantsPage() {
  const headerActionsTarget = useHeaderActionsTarget();
  const { t } = useI18n();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<PowerPlant>>(null);

  const [rowData, setRowData] = useState<PowerPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [weatherMap, setWeatherMap] = useState<Record<string, WeatherData>>({});
  const [forecastSolarDailyMap, setForecastSolarDailyMap] = useState<Record<string, PowerPlantForecastSolarDailyItem | null>>({});
  const [alarmCountsMap, setAlarmCountsMap] = useState<Record<string, PowerPlantAlarmCounts>>({});
  const [availableTags, setAvailableTags] = useState<FilterTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedPlantUuidId, setSelectedPlantUuidId] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [cardView, setCardView] = useState(() => localStorage.getItem('powerplants_cardView') === 'true');
  const [tableGridReady, setTableGridReady] = useState(false);
  const [tablePreferencesLoaded, setTablePreferencesLoaded] = useState(false);
  const [tableColumnState, setTableColumnState] = useState<PowerPlantTableColumnState[] | undefined>(undefined);
  const [cardMetricOrder, setCardMetricOrder] = useState<CardMetricId[]>(DEFAULT_CARD_METRIC_ORDER);
  const weatherMapRef = useRef<Record<string, WeatherData>>({});
  const forecastSolarDailyMapRef = useRef<Record<string, PowerPlantForecastSolarDailyItem | null>>({});
  const alarmCountsMapRef = useRef<Record<string, PowerPlantAlarmCounts>>({});
  const tableSaveTimerRef = useRef<number | null>(null);
  const isApplyingTableStateRef = useRef(false);
  const hasUserModifiedTableStateRef = useRef(false);
  const tableColumnStateRef = useRef<PowerPlantTableColumnState[] | undefined>(undefined);
  const cardMetricOrderRef = useRef<CardMetricId[]>(DEFAULT_CARD_METRIC_ORDER);
  const draggedMetricIdRef = useRef<CardMetricId | null>(null);

  useEffect(() => {
    tableColumnStateRef.current = tableColumnState;
  }, [tableColumnState]);

  useEffect(() => {
    cardMetricOrderRef.current = cardMetricOrder;
  }, [cardMetricOrder]);

  useEffect(() => {
    Promise.all([fetchPowerPlants(), fetchPowerPlantTagFilter(), fetchTagCategories()])
      .then(async ([plants, savedTagIds, categories]) => {
        const powerPlantCategory = categories.find((category) => category.code === 'power_plant');
        const tags = powerPlantCategory ? await fetchTags(powerPlantCategory.id) : [];
        const normalizedTags: FilterTag[] = tags
          .filter((tag) => tag.is_active === 1)
          .map((tag) => ({ id: Number(tag.id), label: tag.label, color: tag.color }));

        setRowData(plants);
        setAvailableTags(normalizedTags.sort((a, b) => a.label.localeCompare(b.label, 'tr')));
        setSelectedTagIds(savedTagIds.map((id) => Number(id)).filter((id) => Number.isInteger(id)));
        setLoading(false);

        const locations = plants.map((p) => weatherLocationKey(p.power_plant_lat, p.power_plant_lon)).filter(Boolean) as string[];
        if (locations.length > 0) {
          fetchWeatherBulk(Array.from(new Set(locations))).then((map) => {
            setWeatherMap(map);
          }).catch(() => {});
        }

        const plantIds = plants.map((plant) => plant.power_plant_uuid_id).filter(Boolean);
        if (plantIds.length > 0) {
          fetchPowerPlantForecastSolarDailyBulk(plantIds)
            .then((map) => {
              setForecastSolarDailyMap(map);
            })
            .catch(() => {});
        }

        // Fetch alarm counts for all plants
        const plantDbIds = plants.map((plant) => plant.power_plant_id).filter(Boolean);
        if (plantDbIds.length > 0) {
          fetchPowerPlantsAlarmCounts(plantDbIds)
            .then((map) => {
              setAlarmCountsMap(map);
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        setError(t('powerPlants.loadError'));
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    fetchPowerPlantTablePreferences()
      .then((preferences) => {
        if (cancelled) return;
        if (hasUserModifiedTableStateRef.current) return;
        const savedColumns = preferences.columnState?.filter((column) => column.colId !== 'power_plant_generation_now');
        if (savedColumns?.length) {
          const pac = savedColumns.find((column) => column.colId === 'power_plant_pac_now');
          const columns = savedColumns.filter((column) => column.colId !== 'power_plant_pac_now');
          const dailyIndex = columns.findIndex((column) => column.colId === 'power_plant_e_day');
          columns.splice(dailyIndex >= 0 ? dailyIndex : columns.length, 0, pac ?? { colId: 'power_plant_pac_now' });
          setTableColumnState(columns);
        } else {
          setTableColumnState(undefined);
        }
        setCardMetricOrder(normalizeOrder(preferences.cardMetricOrder, DEFAULT_CARD_METRIC_ORDER));
      })
      .catch(() => {
        if (!cancelled) setTableColumnState(undefined);
      })
      .finally(() => {
        if (!cancelled) setTablePreferencesLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (cardView) {
      setTableGridReady(false);
    }
  }, [cardView]);

  useEffect(() => {
    weatherMapRef.current = weatherMap;
    const api = gridRef.current?.api;
    if (!api || cardView) return;
    api.refreshCells({ columns: ['weather'], force: true });
  }, [cardView, weatherMap]);

  useEffect(() => {
    forecastSolarDailyMapRef.current = forecastSolarDailyMap;
    const api = gridRef.current?.api;
    if (!api || cardView) return;
    api.refreshCells({
      columns: ['power_plant_forecast_day', 'power_plant_day_realization_pct'],
      force: true,
    });
  }, [cardView, forecastSolarDailyMap]);

  useEffect(() => {
    alarmCountsMapRef.current = alarmCountsMap;
    const api = gridRef.current?.api;
    if (!api || cardView) return;
    api.refreshCells({ columns: ['alarms'], force: true });
  }, [cardView, alarmCountsMap]);

  useEffect(() => {
    if (loading) return;
    void savePowerPlantTagFilter(selectedTagIds);
  }, [loading, selectedTagIds]);

  useEffect(() => {
    localStorage.setItem('powerplants_cardView', String(cardView));
  }, [cardView]);

  useEffect(() => () => {
    if (tableSaveTimerRef.current !== null) {
      window.clearTimeout(tableSaveTimerRef.current);
    }
  }, []);

  const tagFilteredRows = useMemo(() => selectedTagIds.length === 0
      ? rowData
      : rowData.filter((plant) => (plant.power_plant_tags || []).some((tag) => selectedTagIds.includes(tag.id))),
    [rowData, selectedTagIds]);
  const activeCount = tagFilteredRows.filter(plant => plant.power_plant_status === 1).length;
  const statusOptions = [
    { value: 'all', label: 'Tümü', count: tagFilteredRows.length },
    { value: 'active', label: 'Aktif', count: activeCount },
    { value: 'inactive', label: 'Pasif', count: tagFilteredRows.length - activeCount },
  ] as const;
  const filteredRowData = useMemo(() => {
    const tagFiltered = tagFilteredRows.filter(plant => statusFilter === 'all' || (plant.power_plant_status === 1) === (statusFilter === 'active'));

    const normalizedQuery = searchQuery.trim().toLocaleLowerCase('tr');
    if (!normalizedQuery) return tagFiltered;

    return tagFiltered.filter((plant) => {
      const haystack = [
        plant.power_plant_name,
        plant.power_plant_investor_name,
        plant.power_plant_city,
        plant.power_plant_town,
        plant.power_plant_management_company_name,
        plant.power_plant_status === 1 ? 'aktif' : 'pasif',
        ...(plant.power_plant_tags || []).map((tag) => tag.label),
      ]
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .join(' ')
        .toLocaleLowerCase('tr');

      return haystack.includes(normalizedQuery);
    });
  }, [tagFilteredRows, searchQuery, statusFilter]);

  useEffect(() => {
    if (!selectedPlantUuidId) return;
    const stillVisible = filteredRowData.some((plant) => plant.power_plant_uuid_id === selectedPlantUuidId);
    if (!stillVisible) setSelectedPlantUuidId(null);
  }, [filteredRowData, selectedPlantUuidId]);

  const cardMetricPositionMap = useMemo(() => {
    return new Map(cardMetricOrder.map((id, index) => [id, index] as const));
  }, [cardMetricOrder]);

  const colDefs = useMemo<ColDef<PowerPlant>[]>(() => {
    const baseColDefs: ColDef<PowerPlant>[] = [
      {
        field: 'power_plant_name',
        headerName: t('common.name'),
        flex: 2,
        minWidth: 220,
        pinned: 'left',
        cellRenderer: PlantNameCell,
        cellRendererParams: {
          onNavigate: (uuidId: string) => navigate(`/dashboard/powerplants/${uuidId}`),
        },
      },
      { field: 'power_plant_city', headerName: t('common.city'), width: 80, minWidth: 80 },
      {
        field: 'power_plant_investor_name',
        headerName: t('common.investor'),
        flex: 1,
        minWidth: 160,
        cellRenderer: ({ data, value }: ICellRendererParams<PowerPlant, string>) => (
          <EntityLink to={data?.power_plant_investor_uuid_id ? `/dashboard/investor/${data.power_plant_investor_uuid_id}` : null}>{value}</EntityLink>
        ),
      },
      {
        colId: 'weather',
        headerName: 'Hava Durumu',
        width: 124,
        minWidth: 124,
        cellRenderer: ({ data }: { data?: PowerPlant }) => {
          const key = data ? weatherLocationKey(data.power_plant_lat, data.power_plant_lon) : null;
          const w = key ? weatherMapRef.current[key] : null;
          return w ? <WeatherCell weather={w} plantName={data?.power_plant_name || ''} /> : null;
        },
      },
      {
        field: 'power_plant_status',
        headerName: t('common.status'),
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: ({ value }: { value: number }) => (value === 1 ? 'Aktif' : 'Pasif'),
        },
        cellRenderer: ({ value }: any) => <StatusBadge value={value} />,
      },
      {
        colId: 'alarms',
        headerName: 'Alarmlar',
        width: 90,
        minWidth: 90,
        cellRenderer: ({ data }: { data?: PowerPlant }) => {
          const counts = data ? alarmCountsMapRef.current[data.power_plant_uuid_id] : null;
          if (!counts || counts.total === 0) return <span className="text-slate-400 text-sm">—</span>;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/dashboard/alarms?plant_uuid=${data?.power_plant_uuid_id}`);
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 hover:border-rose-300"
            >
              <Bell size={13} />
              <span>{counts.total}</span>
            </button>
          );
        },
      },
      {
        field: 'power_plant_pac_now',
        headerName: t('powerPlants.instantPac'),
        width: 110,
        minWidth: 110,
        valueFormatter: ({ value }) => formatMetricValue(value, 'kW'),
      },
      {
        field: 'power_plant_e_day',
        headerName: t('common.eDay'),
        width: 130,
        minWidth: 130,
        valueGetter: ({ data }) => parseFormattedMetric(data?.power_plant_e_day),
        valueFormatter: ({ value, data }) => data?.power_plant_e_day || formatMetricValue(value, 'kWh'),
      },
      {
        colId: 'power_plant_forecast_day',
        headerName: 'Günlük Tahmin',
        width: 140,
        minWidth: 140,
        hide: true,
        valueGetter: ({ data }) => {
          if (!data?.power_plant_uuid_id) return null;
          return forecastSolarDailyMapRef.current[data.power_plant_uuid_id]?.dailyForecastKwh ?? null;
        },
        valueFormatter: ({ value }) => formatMetricValue(typeof value === 'number' ? value : null, 'kWh'),
      },
      {
        colId: 'power_plant_day_realization_pct',
        headerName: 'Günlük Gerçekleşme',
        width: 160,
        minWidth: 160,
        hide: true,
        valueGetter: ({ data }) => {
          if (!data?.power_plant_uuid_id) return null;
          const dailyForecast = forecastSolarDailyMapRef.current[data.power_plant_uuid_id]?.dailyForecastKwh ?? null;
          return calculateRealizationPercentageFromDailyForecast(data?.power_plant_e_day, dailyForecast);
        },
        valueFormatter: ({ value }) => formatPercentageValue(typeof value === 'number' ? value : null),
        cellRenderer: ({ value }: { value?: number | null }) => <PercentageBadge value={typeof value === 'number' ? value : null} />,
      },
      {
        field: 'power_plant_e_week',
        headerName: t('powerPlants.weekly'),
        width: 130,
        minWidth: 130,
        valueGetter: ({ data }) => parseFormattedMetric(data?.power_plant_e_week),
        valueFormatter: ({ value, data }) => data?.power_plant_e_week || formatMetricValue(value, 'kWh'),
      },
      {
        field: 'power_plant_e_month',
        headerName: t('powerPlants.monthly'),
        width: 130,
        minWidth: 130,
        valueGetter: ({ data }) => parseFormattedMetric(data?.power_plant_e_month),
        valueFormatter: ({ value, data }) => data?.power_plant_e_month || formatMetricValue(value, 'kWh'),
      },
      {
        field: 'power_plant_forecast_month',
        headerName: 'Aylik Tahmin',
        width: 140,
        minWidth: 140,
        hide: true,
        valueGetter: ({ data }) => parseFormattedMetric(data?.power_plant_forecast_month),
        valueFormatter: ({ data, value }) => formatForecastWithSource(
          data?.power_plant_forecast_month || formatMetricValue(value, 'kWh'),
          data?.power_plant_forecast_source,
        ),
      },
      {
        colId: 'power_plant_month_realization_pct',
        headerName: 'Aylik Gerceklesme',
        width: 150,
        minWidth: 150,
        hide: true,
        valueGetter: ({ data }) => calculateRealizationPercentage(data?.power_plant_e_month, data?.power_plant_forecast_month),
        valueFormatter: ({ value }) => formatPercentageValue(typeof value === 'number' ? value : null),
        cellRenderer: ({ value }: { value?: number | null }) => <PercentageBadge value={typeof value === 'number' ? value : null} />,
      },
      {
        field: 'power_plant_e_year',
        headerName: t('powerPlants.yearly'),
        width: 130,
        minWidth: 130,
        valueGetter: ({ data }) => parseFormattedMetric(data?.power_plant_e_year),
        valueFormatter: ({ value, data }) => data?.power_plant_e_year || formatMetricValue(value, 'kWh'),
      },
      {
        field: 'power_plant_forecast_total',
        headerName: 'Yillik Tahmin',
        width: 140,
        minWidth: 140,
        hide: true,
        valueGetter: ({ data }) => parseFormattedMetric(data?.power_plant_forecast_total),
        valueFormatter: ({ data, value }) => formatForecastWithSource(
          data?.power_plant_forecast_total || formatMetricValue(value, 'kWh'),
          data?.power_plant_forecast_source,
        ),
      },
      {
        colId: 'power_plant_year_realization_pct',
        headerName: 'Yillik Gerceklesme',
        width: 150,
        minWidth: 150,
        hide: true,
        valueGetter: ({ data }) => calculateRealizationPercentage(data?.power_plant_e_year, data?.power_plant_forecast_total),
        valueFormatter: ({ value }) => formatPercentageValue(typeof value === 'number' ? value : null),
        cellRenderer: ({ value }: { value?: number | null }) => <PercentageBadge value={typeof value === 'number' ? value : null} />,
      },
      {
        field: 'power_plant_ac_dc_ratio',
        headerName: 'AC/DC',
        width: 90,
        minWidth: 90,
        hide: true,
        valueFormatter: ({ value }) => (typeof value === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value) : '—'),
      },
      { field: 'power_plant_ia_now', headerName: 'Ia', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'A') },
      { field: 'power_plant_ib_now', headerName: 'Ib', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'A') },
      { field: 'power_plant_ic_now', headerName: 'Ic', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'A') },
      { field: 'power_plant_qac_now', headerName: 'Qac', width: 95, minWidth: 95, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'kVAr') },
      { field: 'power_plant_sac_now', headerName: 'Sac', width: 95, minWidth: 95, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'kVA') },
      { field: 'power_plant_pf_now', headerName: 'PF', width: 80, minWidth: 80, hide: true, valueFormatter: ({ value }) => (typeof value === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 3 }).format(value) : '—') },
      { field: 'power_plant_frequency_now', headerName: 'Frekans', width: 100, minWidth: 100, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'Hz') },
      { field: 'power_plant_van_now', headerName: 'Van', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'V') },
      { field: 'power_plant_vbn_now', headerName: 'Vbn', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'V') },
      { field: 'power_plant_vcn_now', headerName: 'Vcn', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'V') },
      { field: 'power_plant_vab_now', headerName: 'Vab', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'V') },
      { field: 'power_plant_vbc_now', headerName: 'Vbc', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'V') },
      { field: 'power_plant_vca_now', headerName: 'Vca', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'V') },
      { field: 'power_plant_gti_now', headerName: 'GTI', width: 90, minWidth: 90, hide: true, valueFormatter: ({ value }) => formatMetricValue(value, 'W/m²') },
      { field: 'power_plant_wpneg', headerName: 'WpNeg', width: 95, minWidth: 95, hide: true },
      { field: 'power_plant_wpneg_pm', headerName: 'WpNeg_PM', width: 110, minWidth: 110, hide: true },
      { field: 'power_plant_wppos', headerName: 'WpPos', width: 95, minWidth: 95, hide: true },
      { field: 'power_plant_wppos_pm', headerName: 'WpPos_PM', width: 110, minWidth: 110, hide: true },
      { field: 'power_plant_wqneg', headerName: 'WqNeg', width: 95, minWidth: 95, hide: true },
      { field: 'power_plant_wqpos', headerName: 'WqPos', width: 95, minWidth: 95, hide: true },
      { field: 'power_plant_h04_cb', headerName: 'H04_CB', width: 100, minWidth: 100, hide: true },
      {
        field: 'power_plant_voltage_now',
        headerName: t('powerPlants.instantVoltage'),
        width: 130,
        minWidth: 130,
        valueFormatter: ({ value }) => formatMetricValue(value, 'V'),
      },
      {
        field: 'power_plant_current_now',
        headerName: t('powerPlants.instantCurrent'),
        width: 130,
        minWidth: 130,
        valueFormatter: ({ value }) => formatMetricValue(value, 'A'),
      },
      {
        field: 'power_plant_consumption_now',
        headerName: t('powerPlants.instantConsumption'),
        width: 145,
        minWidth: 145,
        valueFormatter: ({ value }) => formatMetricValue(value, 'kW'),
      },
      {
        field: 'power_plant_e_total',
        headerName: t('common.eTotal'),
        width: 140,
        minWidth: 140,
        valueGetter: ({ data }) => parseFormattedMetric(data?.power_plant_e_total),
        valueFormatter: ({ value, data }) => data?.power_plant_e_total || formatMetricValue(value, 'kWh'),
      },
      {
        field: 'power_plant_last_seen',
        headerName: t('common.lastSeen'),
        width: 180,
        minWidth: 180,
      },
    ];

    const valueEnabledFields = new Set<string>([
      'power_plant_e_day',
      'power_plant_forecast_day',
      'power_plant_day_realization_pct',
      'power_plant_e_week',
      'power_plant_e_month',
      'power_plant_e_year',
      'power_plant_forecast_month',
      'power_plant_month_realization_pct',
      'power_plant_forecast_total',
      'power_plant_year_realization_pct',
      'power_plant_pac_now',
      'power_plant_ac_dc_ratio',
      'power_plant_ia_now',
      'power_plant_ib_now',
      'power_plant_ic_now',
      'power_plant_qac_now',
      'power_plant_sac_now',
      'power_plant_pf_now',
      'power_plant_frequency_now',
      'power_plant_van_now',
      'power_plant_vbn_now',
      'power_plant_vcn_now',
      'power_plant_vab_now',
      'power_plant_vbc_now',
      'power_plant_vca_now',
      'power_plant_gti_now',
      'power_plant_wpneg',
      'power_plant_wpneg_pm',
      'power_plant_wppos',
      'power_plant_wppos_pm',
      'power_plant_wqneg',
      'power_plant_wqpos',
      'power_plant_voltage_now',
      'power_plant_current_now',
      'power_plant_consumption_now',
      'power_plant_e_total',
    ]);

    const productionFields = new Set(['power_plant_e_day', 'power_plant_e_week', 'power_plant_e_month', 'power_plant_e_year', 'power_plant_e_total']);
    return baseColDefs.map((colDef) => ({
      ...colDef,
      ...(productionFields.has(String(colDef.field)) ? {
        type: 'numericColumn',
        cellDataType: 'number' as const,
        cellClass: 'ag-right-aligned-cell tabular-nums',
        headerClass: 'ag-right-aligned-header',
        valueGetter: ({ data }: { data?: PowerPlant }) => {
          const field = colDef.field as keyof PowerPlant;
          return data?.[`${field}_kwh` as keyof PowerPlant] ?? parseFormattedMetric(data?.[field]);
        },
        valueFormatter: ({ value }: { value: unknown }) => formatEnergyValue(value),
        filter: 'agSetColumnFilter',
        filterParams: { valueFormatter: ({ value }: { value: unknown }) => formatEnergyValue(value) },
      } : {}),
      enableValue: Boolean(colDef.field && valueEnabledFields.has(String(colDef.field))),
    }));
  }, [navigate, t]);

  const defaultColDef = useMemo<ColDef<PowerPlant>>(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    floatingFilter: false,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    suppressHeaderMenuButton: false,
    enablePivot: true,
    enableValue: false,
    enableRowGroup: true,
  }), []);

  useEffect(() => {
    if (!tablePreferencesLoaded || cardView || !tableGridReady) return;
    if (!tableColumnState?.length) return;
    const api = gridRef.current?.api;
    if (!api) return;

    isApplyingTableStateRef.current = true;
    api.applyColumnState({
      state: tableColumnState as PowerPlantTableColumnState[],
      applyOrder: true,
    });

    window.setTimeout(() => {
      isApplyingTableStateRef.current = false;
    }, 0);
  }, [cardView, tableColumnState, tableGridReady, tablePreferencesLoaded]);

  const persistTableColumnState = () => {
    if (cardView || isApplyingTableStateRef.current) return;
    const api = gridRef.current?.api;
    if (!api) return;

    hasUserModifiedTableStateRef.current = true;

    const isPivotMode = typeof (api as { isPivotMode?: () => boolean }).isPivotMode === 'function'
      ? (api as { isPivotMode: () => boolean }).isPivotMode()
      : false;
    if (isPivotMode) return;

    const nextState = api.getColumnState().map((item) => ({
      colId: item.colId,
      hide: item.hide,
      pinned: item.pinned ?? null,
      width: item.width,
    }));

    setTableColumnState(nextState);
    void savePowerPlantTablePreferences({
      columnState: nextState,
      cardMetricOrder: cardMetricOrderRef.current,
    });
  };

  const persistCardMetricOrder = (nextOrder: CardMetricId[]) => {
    setCardMetricOrder(nextOrder);
    void savePowerPlantTablePreferences({
      columnState: tableColumnStateRef.current,
      cardMetricOrder: nextOrder,
    });
  };

  const schedulePersistTableColumnState = () => {
    if (tableSaveTimerRef.current !== null) {
      window.clearTimeout(tableSaveTimerRef.current);
    }

    tableSaveTimerRef.current = window.setTimeout(() => {
      tableSaveTimerRef.current = null;
      persistTableColumnState();
    }, 250);
  };

  const handleResetLayout = async () => {
    const confirmed = await confirmResetLayout(cardView ? 'card' : 'table');
    if (!confirmed) return;

    hasUserModifiedTableStateRef.current = true;
    isApplyingTableStateRef.current = true;
    if (tableSaveTimerRef.current !== null) {
      window.clearTimeout(tableSaveTimerRef.current);
      tableSaveTimerRef.current = null;
    }

    const api = gridRef.current?.api;

    if (cardView) {
      persistCardMetricOrder(DEFAULT_CARD_METRIC_ORDER);
    } else {
      let nextState: PowerPlantTableColumnState[] | undefined;
      if (api) {
        api.resetColumnState();
        nextState = api.getColumnState().map((item) => ({
          colId: item.colId,
          hide: item.hide,
          pinned: item.pinned ?? null,
          width: item.width,
        }));
      }

      setTableColumnState(nextState);
      void savePowerPlantTablePreferences({
        columnState: nextState,
        cardMetricOrder: cardMetricOrderRef.current,
      });
    }

    window.setTimeout(() => {
      isApplyingTableStateRef.current = false;
    }, 0);
  };

  if (loading) return <PageSkeleton />;
  if (error) return <LoadRetry />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActionsTarget && createPortal(
        <div className="flex w-full min-w-0 flex-wrap items-center justify-start gap-1.5 xl:justify-end">
          <label className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-slate-400"><Search size={14}/><input type="search" aria-label="Santral listesinde ara" placeholder="Santral, şehir, yatırımcı…" value={searchQuery} onChange={event=>setSearchQuery(event.target.value)} className="w-32 bg-transparent text-xs text-slate-700 outline-none xl:w-40"/></label>
          <div role="group" aria-label="Santral durumu" className="flex h-8 items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5">{statusOptions.map(option=><button key={option.value} type="button" aria-pressed={statusFilter===option.value} onClick={()=>setStatusFilter(option.value)} className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition ${statusFilter===option.value?'bg-blue-600 text-white shadow-sm':'text-slate-600 hover:bg-white'}`}>{option.label}<span className={`rounded px-1 text-[10px] tabular-nums ${statusFilter===option.value?'bg-white/20':'bg-slate-200/60 text-slate-500'}`}>{option.count}</span></button>)}</div>
          <HomeTagFilter tags={availableTags} selectedIds={selectedTagIds} onChange={setSelectedTagIds}/>
          <button type="button" onClick={()=>setMapOpen(true)} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-blue-100 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-blue-50"><Globe2 size={14} className="text-blue-600"/>Harita</button>
          <button type="button" onClick={()=>void handleResetLayout()} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" title={cardView?'Kart hücre düzenini sıfırla':'Kolon düzenini sıfırla'} aria-label={cardView?'Kart hücre düzenini sıfırla':'Kolon düzenini sıfırla'}><RefreshCcw size={14}/></button>
          <button type="button" onClick={()=>setCardView(prev=>!prev)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" title={cardView?'Tablo Görünümü':'Kart Görünümü'} aria-label={cardView?'Tablo Görünümü':'Kart Görünümü'}>{cardView?<Table2 size={14}/>:<LayoutGrid size={14}/>}</button>
        </div>,headerActionsTarget)}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex-1 min-h-0">
          {cardView ? (
            <div className="h-full overflow-auto custom-scrollbar space-y-3 pr-1">
              {filteredRowData.map((plant) => {
                const stale = isPlantDataStale(plant.power_plant_last_seen);
                const tags = Array.isArray(plant.power_plant_tags) ? plant.power_plant_tags : [];
                const visibleTags = tags.slice(0, 3);
                const remainingTags = tags.length - visibleTags.length;
                const weatherKey = weatherLocationKey(plant.power_plant_lat, plant.power_plant_lon);
                const weather = weatherKey ? weatherMap[weatherKey] : null;
                const dailyForecast = forecastSolarDailyMap[plant.power_plant_uuid_id]?.dailyForecastKwh ?? null;
                const dailyRealization = calculateRealizationPercentageFromDailyForecast(plant.power_plant_e_day, dailyForecast);
                const monthlyRealization = calculateRealizationPercentage(plant.power_plant_e_month, plant.power_plant_forecast_month);
                const yearlyRealization = calculateRealizationPercentage(plant.power_plant_e_year, plant.power_plant_forecast_total);
                const metricDefs: Array<{ id: CardMetricId; label: string; value: ReactNode }> = [
                  { id: 'city', label: 'Şehir', value: plant.power_plant_city || '—' },
                  { id: 'investor', label: 'Yatırımcı', value: <EntityLink to={plant.power_plant_investor_uuid_id ? `/dashboard/investor/${plant.power_plant_investor_uuid_id}` : null}>{plant.power_plant_investor_name}</EntityLink> },
                  { id: 'weather', label: 'Hava', value: weather ? <WeatherCell weather={weather} plantName={plant.power_plant_name || ''} /> : '—' },
                  { id: 'e_day', label: 'E Gün', value: plant.power_plant_e_day || '—' },
                  { id: 'daily_forecast', label: 'Günlük Tahmin', value: formatMetricValue(dailyForecast, 'kWh') },
                  { id: 'daily_realization', label: 'Günlük Gerçekleşme', value: <PercentageBadge value={dailyRealization} /> },
                  { id: 'e_week', label: 'E Hafta', value: plant.power_plant_e_week || '—' },
                  { id: 'e_month', label: 'E Ay', value: plant.power_plant_e_month || '—' },
                  { id: 'monthly_forecast', label: 'Aylık Tahmin', value: formatForecastWithSource(plant.power_plant_forecast_month, plant.power_plant_forecast_source) },
                  { id: 'monthly_realization', label: 'Aylık Gerçekleşme', value: <PercentageBadge value={monthlyRealization} /> },
                  { id: 'e_year', label: 'E Yıl', value: plant.power_plant_e_year || '—' },
                  { id: 'yearly_forecast', label: 'Yıllık Tahmin', value: formatForecastWithSource(plant.power_plant_forecast_total, plant.power_plant_forecast_source) },
                  { id: 'yearly_realization', label: 'Yıllık Gerçekleşme', value: <PercentageBadge value={yearlyRealization} /> },
                  { id: 'e_total', label: 'E Toplam', value: plant.power_plant_e_total || '—' },
                  { id: 'pac', label: 'Pac', value: formatMetricValue(plant.power_plant_pac_now, 'kW') },
                  { id: 'ac_dc_ratio', label: 'AC/DC', value: typeof plant.power_plant_ac_dc_ratio === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(plant.power_plant_ac_dc_ratio) : '—' },
                  { id: 'ia', label: 'Ia', value: formatMetricValue(plant.power_plant_ia_now, 'A') },
                  { id: 'ib', label: 'Ib', value: formatMetricValue(plant.power_plant_ib_now, 'A') },
                  { id: 'ic', label: 'Ic', value: formatMetricValue(plant.power_plant_ic_now, 'A') },
                  { id: 'qac', label: 'Qac', value: formatMetricValue(plant.power_plant_qac_now, 'kVAr') },
                  { id: 'sac', label: 'Sac', value: formatMetricValue(plant.power_plant_sac_now, 'kVA') },
                  { id: 'pf', label: 'PF', value: typeof plant.power_plant_pf_now === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 3 }).format(plant.power_plant_pf_now) : '—' },
                  { id: 'frequency', label: 'Frekans', value: formatMetricValue(plant.power_plant_frequency_now, 'Hz') },
                  { id: 'van', label: 'Van', value: formatMetricValue(plant.power_plant_van_now, 'V') },
                  { id: 'vbn', label: 'Vbn', value: formatMetricValue(plant.power_plant_vbn_now, 'V') },
                  { id: 'vcn', label: 'Vcn', value: formatMetricValue(plant.power_plant_vcn_now, 'V') },
                  { id: 'vab', label: 'Vab', value: formatMetricValue(plant.power_plant_vab_now, 'V') },
                  { id: 'vbc', label: 'Vbc', value: formatMetricValue(plant.power_plant_vbc_now, 'V') },
                  { id: 'vca', label: 'Vca', value: formatMetricValue(plant.power_plant_vca_now, 'V') },
                  { id: 'gti', label: 'GTI', value: formatMetricValue(plant.power_plant_gti_now, 'W/m²') },
                  { id: 'generation', label: 'Üretim', value: formatMetricValue(plant.power_plant_generation_now, 'kW') },
                  { id: 'voltage', label: 'Gerilim', value: formatMetricValue(plant.power_plant_voltage_now, 'V') },
                  { id: 'current', label: 'Akım', value: formatMetricValue(plant.power_plant_current_now, 'A') },
                  { id: 'consumption', label: 'Tüketim', value: formatMetricValue(plant.power_plant_consumption_now, 'kW') },
                  { id: 'wpneg', label: 'WpNeg', value: typeof plant.power_plant_wpneg === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(plant.power_plant_wpneg) : '—' },
                  { id: 'wpneg_pm', label: 'WpNeg_PM', value: typeof plant.power_plant_wpneg_pm === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(plant.power_plant_wpneg_pm) : '—' },
                  { id: 'wppos', label: 'WpPos', value: typeof plant.power_plant_wppos === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(plant.power_plant_wppos) : '—' },
                  { id: 'wppos_pm', label: 'WpPos_PM', value: typeof plant.power_plant_wppos_pm === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(plant.power_plant_wppos_pm) : '—' },
                  { id: 'wqneg', label: 'WqNeg', value: typeof plant.power_plant_wqneg === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(plant.power_plant_wqneg) : '—' },
                  { id: 'wqpos', label: 'WqPos', value: typeof plant.power_plant_wqpos === 'number' ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(plant.power_plant_wqpos) : '—' },
                  { id: 'h04_cb', label: 'H04_CB', value: plant.power_plant_h04_cb || '—' },
                  { id: 'last_seen', label: 'Son Görülme', value: plant.power_plant_last_seen || '—' },
                ];
                const orderedMetrics = [...metricDefs].sort((left, right) => {
                  const leftIndex = cardMetricPositionMap.get(left.id) ?? Number.MAX_SAFE_INTEGER;
                  const rightIndex = cardMetricPositionMap.get(right.id) ?? Number.MAX_SAFE_INTEGER;
                  return leftIndex - rightIndex;
                });

                return (
                  <div
                    key={plant.power_plant_uuid_id}
                    className="group rounded-2xl border border-slate-100 bg-white p-3 shadow-sm transition-all hover:border-slate-200 hover:shadow-md"
                    onClick={() => setSelectedPlantUuidId(plant.power_plant_uuid_id)}
                    onDoubleClick={() => navigate(`/dashboard/powerplants/${plant.power_plant_uuid_id}`)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-start gap-2">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/dashboard/powerplants/${plant.power_plant_uuid_id}`);
                            }}
                            className="mt-0.5 inline-flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 p-1 text-blue-700 hover:bg-blue-100"
                            title="Detaya Git"
                            aria-label="Detaya Git"
                          >
                            <ArrowUpRight size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/dashboard/powerplants/${plant.power_plant_uuid_id}/flow`);
                            }}
                            className="mt-0.5 inline-flex items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 p-1 text-emerald-700 hover:bg-emerald-100"
                            title="Akış Ekranı"
                            aria-label="Akış Ekranı"
                          >
                            <Activity size={13} />
                          </button>
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 text-sm font-black text-slate-900">
                            {stale && (
                              <span
                                className="inline-flex items-center text-rose-600"
                                title="Bu santralden 2 saatten uzun suredir veri alinmiyor"
                              >
                                <WifiOff size={14} />
                              </span>
                            )}
                            <span>{plant.power_plant_name}</span>
                          </div>
                          <div className="flex min-w-0 items-center gap-1 text-xs font-semibold text-slate-400"><EntityLink to={plant.power_plant_investor_uuid_id ? `/dashboard/investor/${plant.power_plant_investor_uuid_id}` : null}>{plant.power_plant_investor_name}</EntityLink><span>• {plant.power_plant_city}</span></div>
                          {visibleTags.length > 0 && (
                            <div className="mt-1 flex items-center gap-1.5">
                              {visibleTags.map((tag) => (
                                <TagColorDot key={tag.id} color={tag.color} size={14} />
                              ))}
                              {remainingTags > 0 && <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap">+{remainingTags}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      <StatusBadge value={plant.power_plant_status} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
                      {orderedMetrics.map((metric) => (
                        <div
                          key={metric.id}
                          draggable
                          onDragStart={(event) => {
                            draggedMetricIdRef.current = metric.id;
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData('text/plain', metric.id);
                          }}
                          onDragOver={(event) => {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            const draggedId = draggedMetricIdRef.current || event.dataTransfer.getData('text/plain');
                            if (!draggedId || draggedId === metric.id) return;
                            const nextOrder = moveItemBefore(cardMetricOrderRef.current, draggedId as CardMetricId, metric.id);
                            persistCardMetricOrder(nextOrder);
                          }}
                          onDragEnd={() => {
                            draggedMetricIdRef.current = null;
                          }}
                          className="group/metric rounded-lg border border-slate-100 bg-slate-50/60 px-2 py-1 cursor-move hover:border-slate-200 hover:bg-slate-50 active:cursor-grabbing"
                          title="Sürükle bırak ile yer değiştir"
                        >
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{metric.label}</div>
                            <div className="inline-flex items-center gap-1 text-slate-300 transition group-hover/metric:text-slate-500">
                              <GripVertical className="h-3 w-3" />
                              <LayoutGrid className="h-3 w-3" />
                            </div>
                          </div>
                          <div className="text-[11px] font-black text-slate-800 truncate">{metric.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <AgGridReact<PowerPlant>
              ref={gridRef}
              modules={enterpriseModules}
              theme={gridTheme}
              maintainColumnOrder
              rowData={filteredRowData}
              getRowId={({ data }) => data.power_plant_uuid_id}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              pagination
              paginationPageSize={100}
              paginationPageSizeSelector={[25, 50, 100]}
              animateRows
              rowStyle={{ cursor: 'pointer' }}
              getRowStyle={({ data }) => data?.power_plant_uuid_id === selectedPlantUuidId ? { backgroundColor: '#eff6ff' } : undefined}
              onGridReady={() => setTableGridReady(true)}
              onCellClicked={(e) => {
                if (e.column.getColId() === 'weather') return;
                if (e.data?.power_plant_uuid_id) setSelectedPlantUuidId(e.data.power_plant_uuid_id);
              }}
              onCellDoubleClicked={(e: CellDoubleClickedEvent<PowerPlant>) => {
                if (e.column.getColId() === 'weather') return;
                if (e.data?.power_plant_uuid_id) navigate(`/dashboard/powerplants/${e.data.power_plant_uuid_id}`);
              }}
              onColumnMoved={schedulePersistTableColumnState}
              onColumnVisible={schedulePersistTableColumnState}
              onColumnResized={(event) => {
                if (!event.finished) return;
                schedulePersistTableColumnState();
              }}
              sideBar={{
                toolPanels: [
                  { id: 'columns', labelDefault: t('common.columns'), labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
                  { id: 'filters', labelDefault: t('common.filters'), labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
                ],
              }}
              pivotPanelShow="onlyWhenPivoting"
            />
          )}
        </div>
      </div>

      {mapOpen && createPortal(
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="relative h-[92vh] w-full max-w-7xl flex flex-col overflow-hidden rounded-[32px] bg-white shadow-2xl">
            <button
              type="button"
              onClick={() => setMapOpen(false)}
              className="absolute right-6 top-6 z-10 rounded-xl border border-slate-100 bg-white p-2 text-slate-500 shadow-sm transition-all hover:bg-slate-50"
            >
              <X size={18} />
            </button>
            <div className="flex-1 p-8 pt-16">
              <WorldPlantMap plants={filteredRowData} onPlantSelect={(uuidId) => { setMapOpen(false); setSelectedPlantUuidId(uuidId); }} />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
